import { Grid, FormControl, Select, MenuItem, Box, Typography } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import React, { useState, useMemo, useEffect, useRef } from 'react'
import { getNextVisitDateDifference, formatTimeSlotInClinicTimezone } from 'src/mentor/components/commonReportComponents/utils/nextVisitRosterCalendarUtils'
import { getCurrencyObject, getPriceStringWithCurrency, getSubscriptionPlanById } from 'src/mentor/helpers/utilityFunctions'
import { CustomInput } from 'src/consumer/components/helperComponents'
import { SESSION_PLATFORM_OFFLINE, SESSION_PLATFORM_ONLINE, displayNamesOfEventTypes } from 'src/consumer/constants'
import VideocamIcon from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'

export default function NextVisitHoldSlotConfirmationView({
  selectedSlot,
  selectedDate,
  bookingDate,
  currentEventTypeObject,
  originalEventTypeObject,
  bookingFormData,
  handleBookingFormChange,
  mentorProfile,
  selectedEventTypeId,
  setSelectedEventTypeId,
  clinicLocation = null, // Clinic location for timezone
  currentFee = null, // Current booked event fee
  currentCurrency = null, // Current booked event currency
}) {
  const [subscriptionPlans, setSubscriptionPlans] = useState({}) // Map of eventTypeId -> subscription plan data
  const [initialFee, setInitialFee] = useState(null) // Track initial fee for display message
  const previousEventTypeIdRef = useRef(null) // Track previous event type to detect changes
  const userManuallyEditedFeeRef = useRef(false) // Track if user has manually edited the fee

  // Get available event types for selection
  // Filter by eventType and clinicBrandId matching currentEventTypeObject
  const availableEventTypes = useMemo(() => {
    if (!mentorProfile?.eventTypes) return []
    
    let filtered = mentorProfile.eventTypes.filter(et => et.enabled !== false)
    
    // Filter by eventType if currentEventTypeObject has a type
    if (currentEventTypeObject?.type) {
      filtered = filtered.filter(et => et.type === currentEventTypeObject.type)
    }
    
    // Filter by clinicBrandId if currentEventTypeObject or clinicLocation has clinicBrandId
    const targetClinicBrandId = currentEventTypeObject?.clinicBrandId || clinicLocation?.clinicBrandId
    if (targetClinicBrandId) {
      filtered = filtered.filter(et => {
        // Check if event type has clinicBrandId that matches, or if it doesn't have one (global)
        return !et.clinicBrandId || et.clinicBrandId === targetClinicBrandId
      })
    }
    
    return filtered
  }, [mentorProfile, currentEventTypeObject, clinicLocation])

  // Fetch subscription plans for all available event types
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      const plansMap = {}
      
      // Fetch plans for all event types in parallel
      const fetchPromises = availableEventTypes.map(async (eventType) => {
        const planId = eventType?.subscriptionPlanId || eventType?.pricingPlanId
        if (planId && !eventType?.free) {
          try {
            const response = await getSubscriptionPlanById(planId)
            if (response?.status === 200 && response?.data) {
              plansMap[eventType.uid] = response.data
            }
          } catch (error) {
            console.error(`Error fetching subscription plan for event type ${eventType.uid}:`, error)
          }
        }
      })
      
      await Promise.all(fetchPromises)
      setSubscriptionPlans(plansMap)
    }

    if (availableEventTypes.length > 0) {
      fetchSubscriptionPlans()
    }
  }, [availableEventTypes])

  // Get selected event type object
  const selectedEventTypeObject = useMemo(() => {
    return availableEventTypes.find(et => et.uid === selectedEventTypeId) || currentEventTypeObject
  }, [availableEventTypes, selectedEventTypeId, currentEventTypeObject])

  // Initialize fee on first load
  useEffect(() => {
    if (!selectedEventTypeObject || previousEventTypeIdRef.current !== null) return
    
    // Check if this is the original event type
    const isOriginalEventType = originalEventTypeObject?.uid === selectedEventTypeObject.uid
    
    // If it's the original event type, use current fee
    if (isOriginalEventType && currentFee !== null && currentFee !== undefined) {
      setInitialFee(parseFloat(currentFee) || 0)
      handleBookingFormChange({
        target: {
          id: 'fee',
          value: currentFee.toString(),
        },
      })
      if (currentCurrency && currentCurrency !== bookingFormData?.currency) {
        handleBookingFormChange({
          target: {
            id: 'currency',
            value: currentCurrency,
          },
        })
      }
      return
    }
    
    // For other event types, use subscription plan
    const subscriptionPlan = subscriptionPlans[selectedEventTypeObject.uid]
    
    if (subscriptionPlan) {
      const fee = subscriptionPlan?.finalPrice || subscriptionPlan?.price
      const currency = subscriptionPlan?.currency || bookingFormData?.currency || 'inr'
      
      if (fee !== undefined && fee !== null) {
        setInitialFee(fee)
        handleBookingFormChange({
          target: {
            id: 'fee',
            value: fee.toString(),
          },
        })
        if (currency !== bookingFormData?.currency) {
          handleBookingFormChange({
            target: {
              id: 'currency',
              value: currency,
            },
          })
        }
      }
    } else if (selectedEventTypeObject?.free) {
      setInitialFee(0)
      handleBookingFormChange({
        target: {
          id: 'fee',
          value: '0',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionPlans, selectedEventTypeObject?.uid])

  // Custom handler for fee changes to track manual edits
  const handleFeeChange = (e) => {
    // Mark that user has manually edited the fee
    if (e.target.id === 'fee') {
      userManuallyEditedFeeRef.current = true
    }
    handleBookingFormChange(e)
  }

  // Update fee when event type changes (only if user hasn't manually edited)
  useEffect(() => {
    if (!selectedEventTypeObject) return
    
    const currentEventTypeId = selectedEventTypeObject.uid
    
    // Only update if event type actually changed
    if (previousEventTypeIdRef.current === currentEventTypeId) {
      return
    }
    
    // Reset manual edit flag when event type changes (allow auto-update for new event type)
    userManuallyEditedFeeRef.current = false
    previousEventTypeIdRef.current = currentEventTypeId
    
    // Check if this is the original event type
    const isOriginalEventType = originalEventTypeObject?.uid === currentEventTypeId
    
    // If it's the original event type, use current fee
    if (isOriginalEventType && currentFee !== null && currentFee !== undefined) {
      if (initialFee === null) {
        setInitialFee(parseFloat(currentFee) || 0)
      }
      handleBookingFormChange({
        target: {
          id: 'fee',
          value: currentFee.toString(),
        },
      })
      if (currentCurrency && currentCurrency !== bookingFormData?.currency) {
        handleBookingFormChange({
          target: {
            id: 'currency',
            value: currentCurrency,
          },
        })
      }
      return
    }
    
    // For other event types, use subscription plan
    const subscriptionPlan = subscriptionPlans[currentEventTypeId]
    
    // Get fee from subscription plan
    if (subscriptionPlan) {
      const fee = subscriptionPlan?.finalPrice || subscriptionPlan?.price
      const currency = subscriptionPlan?.currency || bookingFormData?.currency || 'inr'
      
      if (fee !== undefined && fee !== null) {
        // Set initial fee if not set yet
        if (initialFee === null) {
          setInitialFee(fee)
        }
        
        // Update booking form data with fee from subscription plan
        handleBookingFormChange({
          target: {
            id: 'fee',
            value: fee.toString(),
          },
        })
        
        // Update currency if different
        if (currency !== bookingFormData?.currency) {
          handleBookingFormChange({
            target: {
              id: 'currency',
              value: currency,
            },
          })
        }
      }
    } else if (selectedEventTypeObject?.free) {
      // If event type is free, set fee to 0
      if (initialFee === null) {
        setInitialFee(0)
      }
      handleBookingFormChange({
        target: {
          id: 'fee',
          value: '0',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventTypeObject?.uid, subscriptionPlans])

  // Format slot time in clinic timezone
  const formattedSlotTime = useMemo(() => {
    if (!selectedSlot) return null
    
    // Use clinicLocation from slot if available, otherwise use prop
    const location = selectedSlot.clinicLocation || clinicLocation
    
    // If slot has start/end times, format them
    if (selectedSlot.start && selectedSlot.end) {
      return formatTimeSlotInClinicTimezone(
        selectedSlot.start,
        selectedSlot.end,
        location,
        selectedEventTypeObject?.durationInMins || currentEventTypeObject?.durationInMins || 30
      )
    }
    
    // If slot has label, try to extract times or use label as-is
    // But prefer formatted version if we can get it
    if (selectedSlot.label) {
      // If label already includes timezone info, use it
      if (selectedSlot.labelWithTz) {
        return { label: selectedSlot.labelWithTz, labelWithTz: selectedSlot.labelWithTz }
      }
      // Otherwise, if we have start time, format it
      if (selectedSlot.start) {
        return formatTimeSlotInClinicTimezone(
          selectedSlot.start,
          selectedSlot.end,
          location,
          selectedEventTypeObject?.durationInMins || currentEventTypeObject?.durationInMins || 30
        )
      }
      // Fallback to original label
      return { label: selectedSlot.label, labelWithTz: selectedSlot.label }
    }
    
    return null
  }, [selectedSlot, clinicLocation, selectedEventTypeObject, currentEventTypeObject])

  return (
    <div style={{ padding: '16px 0 100px', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          textAlign: 'center',
          padding: '16px',
          backgroundColor: '#e2dfff',
          borderRadius: '12px',
          border: '2px solid #a167f4',
          margin: '0 20px 16px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#6b21a8',
            marginBottom: '16px',
          }}
        >
          Confirm The Slot & Payment Details
        </div>
        <div
          style={{
            fontSize: '20px',
            fontWeight: 700,
            margin: '8px 0',
            color: 'rgb(141, 68, 173)',
            lineHeight: 1.4,
          }}
        >
          {formattedSlotTime?.labelWithTz || formattedSlotTime?.label || selectedSlot?.label}
        </div>
        <div style={{ fontSize: '14px', color: '#666', marginTop: '6px', lineHeight: 1.5 }}>
          {selectedDate?.format('dddd, MMMM D, YYYY')} • {getNextVisitDateDifference(selectedDate, bookingDate)}
        </div>
        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
          Duration: {selectedEventTypeObject?.durationInMins || currentEventTypeObject?.durationInMins || 30} mins
        </div>
      </div>

      {/* Event Type Selector */}
      {availableEventTypes.length > 1 && (
        <Box
          sx={{
            margin: '0 20px 16px',
            padding: '12px 16px',
            backgroundColor: 'rgba(139, 92, 246, 0.05)',
            borderRadius: '10px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <CalendarMonthIcon sx={{ fontSize: '18px', color: '#7c3aed' }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6b21a8' }}>
              Appointment Type
            </Typography>
          </Box>
          <FormControl fullWidth>
            <Select
              value={selectedEventTypeId || currentEventTypeObject?.uid || ''}
              onChange={(e) => setSelectedEventTypeId(e.target.value)}
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.25)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(139, 92, 246, 0.25)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(139, 92, 246, 0.4)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(139, 92, 246, 0.6)',
                },
              }}
            >
              {availableEventTypes.map((eventType) => {
                // Check if this is the original event type
                const isOriginalEventType = originalEventTypeObject?.uid === eventType.uid
                
                let priceString = null
                
                // For original event type, show current fee
                if (isOriginalEventType && currentFee !== null && currentFee !== undefined) {
                  const fee = parseFloat(currentFee) || 0
                  const currency = currentCurrency || 'inr'
                  if (fee > 0) {
                    priceString = getPriceStringWithCurrency(fee, currency)
                  } else {
                    priceString = 'Free'
                  }
                } else {
                  // For other event types, get price from subscription plan
                  const subscriptionPlan = subscriptionPlans[eventType.uid]
                  
                  if (subscriptionPlan) {
                    // Get price from subscription plan
                    const price = subscriptionPlan?.finalPrice || subscriptionPlan?.price
                    const currency = subscriptionPlan?.currency || 'inr'
                    if (price) {
                      priceString = getPriceStringWithCurrency(price, currency)
                    }
                  } else if (eventType?.free) {
                    priceString = 'Free'
                  }
                }
                
                return (
                  <MenuItem key={eventType.uid} value={eventType.uid}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                      {eventType.meetingSrc === SESSION_PLATFORM_OFFLINE ? (
                        <LocationOnIcon sx={{ fontSize: '18px', color: '#d32f2f' }} />
                      ) : (
                        <VideocamIcon sx={{ fontSize: '18px', color: '#1976d2' }} />
                      )}
                      <Typography sx={{ flex: 1, color: '#6b21a8', fontWeight: 500 }}>
                        {eventType.title || displayNamesOfEventTypes?.[eventType.type] || eventType.type}
                        {priceString && (
                          <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 600, color: '#4caf50' }}>
                            - {priceString}
                          </span>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                        ({eventType.durationInMins || 30} mins)
                      </Typography>
                    </Box>
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Payment Details Form - Always Visible */}
      <Box
        sx={{
          margin: '0 20px 16px',
          padding: '12px 16px',
          backgroundColor: 'rgba(139, 92, 246, 0.05)',
          borderRadius: '10px',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          flexShrink: 0,
          marginTop: 'auto',
        }}
      >
        <Box sx={{ marginBottom: '10px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6b21a8', marginBottom: '6px' }}>
            Fee Details
          </Typography>
          {initialFee !== null && (
            <Typography 
              sx={{ 
                fontSize: '11px', 
                color: '#6b7280', 
                fontStyle: 'italic',
                marginBottom: '8px',
              }}
            >
              Fee for current session was {getPriceStringWithCurrency(initialFee, bookingFormData?.currency || 'inr')}
            </Typography>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
              <CustomInput
                id="fee"
                conponentContainerStyles={{ padding: '0px', width: '100%' }}
                inputAnsMessageContainerStyles={{ padding: '0px 0px 0px 8px', gap: '0px' }}
                value={bookingFormData?.fee ?? ''}
                preFixText={getCurrencyObject(bookingFormData?.currency)?.symbol}
                label={`Fee`}
                type="number"
                labelPlacement="horizontal"
                onChange={handleFeeChange}
                placeholder="Enter fee amount"
              />
            </div>
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}
